import React, { FormEvent, useState } from "react";
import HintBox from "@/src/components/Box/HintBox/HintBox";
import FileUploadSection from "@/src/components/Form/Common/FileUploadSection/FileUploadSection";
import { useForm } from "@/src/provider/Form/FormProvider";
import Typography from "@mui/material/Typography";
import { Box, Stack } from "@mui/material";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import useMediaObjectCombinationRequest from "@/src/hooks/useMediaObjectCombinationRequest";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { getCaseLink } from "@/src/services/productService";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";

export default function Documents({ product, submitProps }: { product: AbstractCase; submitProps: FormSubmitProps }) {
  let mediaObjectCombinationRequestTypes = ["files"];

  const { mediaObjectCombinationRequests, isLoadingMediaObjectCombinationRequest, hasAtLeastOneFileOfType, updateAction } = useMediaObjectCombinationRequest(
    product,
    mediaObjectCombinationRequestTypes
  );

  const { handleSubmit } = useForm();
  const [isBlocked, setIsBlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isSubmittable = !(isBlocked || !hasAtLeastOneFileOfType(mediaObjectCombinationRequests, "files"));

  const handleSubmitInternal = async (e: FormEvent<HTMLFormElement>) => {
    setIsLoading(true);
    await handleSubmit(e);
  };

  const hasFineNotice = product.authorityNotification === "fine_notice";
  if (isLoadingMediaObjectCombinationRequest) {
    return <LegalbirdLoader />;
  }

  return (
    <>
      <HintBox headline={"Ihre Angaben sind vollständig. Jetzt " + (hasFineNotice ? "Bußgeldbescheid" : "Dokument") + " hochladen!"}>
        {product.authorityNotification !== "no_notice" ? (
          <>
            Damit wir Ihnen eine kostenlose Ersteinschätzung geben können, benötigen wir jetzt noch
            {hasFineNotice
              ? " Ihren Bußgeldbescheid. "
              : " das dazugehörige Dokument. Laden Sie hier bitte den Bußgeldbescheid, Anhörungsbogen, Zeugenfragebogen oder ähnliches hoch. "}
            Wir melden uns dann zeitnah bei Ihnen, um das weitere Vorgehen zu besprechen.
          </>
        ) : (
          <>
            Da Sie bisher kein Dokument von der Behörde erhalten haben, melden wir uns nun zeitnah bei Ihnen, um das weitere Vorgehen zu besprechen. Sobald Sie
            Post von der Behörde bekommen haben, können Sie hier den Anhörungsbogen, Zeugenfragebogen oder Bußgeldbescheid hochladen und wir prüfen Ihren
            Bescheid kostenlos.
          </>
        )}
      </HintBox>
      <ContentBox>
        <Stack
          component={"form"}
          onSubmit={handleSubmitInternal}
          sx={{
            justifyContent: "center",
            alignItems: "stretch",
            textAlign: "left",
          }}>
          <Typography variant={"h5"} sx={(theme) => ({ color: theme.palette.text.primary })}>
            Wichtig
          </Typography>
          <Box
            sx={{
              textAlign: "left",
            }}>
            Laden Sie hier {hasFineNotice ? "Ihren Bußgeldbescheid" : "Ihr Dokument"} als Bild oder PDF hoch, oder fotografieren Sie{" "}
            {hasFineNotice ? "ihn" : "es"} einfach ab. Bitte beachten Sie dabei, dass alles gut erkennbar und nichts abgeschnitten ist. Benutzen Sie bestenfalls
            beim Fotografieren den Blitz.
            <Box sx={{
              textAlign: "right"
            }}>
              <FileUploadSection
                mediaObjectCombinationRequest={mediaObjectCombinationRequests.files}
                temporaryMediaObjectsFieldname={"files"}
                headline={""}
                helperText={""}
                productEntity={product}
                productEntityId={product.id}
                product={"traffic"}
                updateAction={updateAction}
                setIsBlocked={setIsBlocked}
                isBlocked={isBlocked}
                fieldName={hasFineNotice ? "fineNotice" : "trafficGeneralFile"}
              />
            </Box>
          </Box>
          <FormSubmit
            {...submitProps}
            disabled={!isSubmittable}
            isLoading={isLoading}
            nextText={"Kostenlose Ersteinschätzung"}
            backLink={getCaseLink(product) + "/antrag/schreiben"}
          />
        </Stack>
      </ContentBox>
    </>
  );
}
